import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl'; // Add useIntl
import css from './WalletToggle.module.css';
import {
  updateProfile,
  updateProfileInProgressSelector,
} from '../../../ProfileSettingsPage/ProfileSettingsPage.duck';
import { currentUserSelector } from '../../../../ducks/user.duck';
import { showErrorToast } from '../../../../util/toast';
import { getWalletAvailability } from '../../../../util/dataExtractor';
import { Tooltip as ReactTooltip } from 'react-tooltip';

const WalletToggle = ({ hasPaymentMethodConnected }) => {
  const dispatch = useDispatch();
  const intl = useIntl(); // For formatting messages in JS
  const currentUser = useSelector(currentUserSelector);
  const updateInProgress = useSelector(updateProfileInProgressSelector);
  const walletPaymentsEnabled = getWalletAvailability(currentUser);

  const [isEnabled, setIsEnabled] = useState(walletPaymentsEnabled);

  const handleToggle = useCallback(async () => {
    const newValue = !isEnabled;
    setIsEnabled(newValue);

    try {
      await dispatch(
        updateProfile({
          publicData: {
            walletPaymentsEnabled: newValue,
          },
        })
      );
    } catch (error) {
      setIsEnabled(!newValue);
      showErrorToast(
        <FormattedMessage
          id="WalletToggle.updateError"
          defaultMessage="Failed to update wallet preference. Please try again."
        />
      );
    }
  }, [isEnabled, dispatch]);

  const toggleClasses = `${css.toggleSwitch} ${isEnabled ? css.enabled : ''}`;

  // Determine if disabled due to missing payment method
  const isDisabledDueToNoPayment = !hasPaymentMethodConnected;
  const isToggleDisabled = updateInProgress || isDisabledDueToNoPayment;

  // Unique ID for the tooltip
  const tooltipId = 'wallet-toggle-tooltip';

  return (
    <div className={css.offerToggleContainer}>
      <div className={css.toggleWrapper}>
        <label className={css.toggleLabel} htmlFor="wallet-toggle">
          <span className={css.labelText}>
            <FormattedMessage id="WalletToggle.enableWallet" defaultMessage="Enable Wallet" />
          </span>
          <p className={css.description}>
            <FormattedMessage
              id="WalletToggle.enableOfferDescription"
              defaultMessage="Turn on to collect payments in your wallet. Funds can be withdrawn anytime."
            />
          </p>
        </label>

        <button
          id="wallet-toggle"
          type="button"
          role="switch"
          aria-checked={isEnabled}
          aria-label="Toggle wallet availability"
          className={toggleClasses}
          onClick={handleToggle}
          disabled={isToggleDisabled}
          data-tooltip-id={isDisabledDueToNoPayment ? tooltipId : undefined} // Only attach tooltip when relevant
        >
          <span className={css.toggleThumb} aria-hidden="true" />
        </button>
      </div>

      {updateInProgress && (
        <span className={css.updatingText} role="status" aria-live="polite">
          <FormattedMessage id="WalletToggle.updating" defaultMessage="Updating..." />
        </span>
      )}

      {/* Tooltip - only renders when needed */}
      {isDisabledDueToNoPayment && (
        <ReactTooltip
          id={tooltipId}
          place="top"
          effect="solid"
          content={intl.formatMessage({ id: 'WalletToggle.noPaymentMethodTooltip' })}
        />
      )}
    </div>
  );
};

export default WalletToggle;
